import { useEffect, useState, useRef } from 'react'
import { apiClient } from '../api/client'
import { Modal } from './modal'
import { Button } from './button'

// 字段对齐后端 server/internal/captcha/captcha.go Challenge struct。
// image_base64 / thumb_base64 已含 "data:image/jpeg;base64," 前缀,直接给 <img src> 用。
type Challenge = {
  challenge_id: string
  image_base64: string
  thumb_base64: string
  prompt: string
}

type Dot = { x: number; y: number }

export type CaptchaModalProps = {
  open: boolean
  onClose: () => void
  // 2026-05-28 dg-fix-user2 §4 梯度 captcha:onVerified 返回 N 个 token 数组;
  // requiredCount > 1 时 modal 自动循环 N 轮收集
  onVerified: (tokens: string[]) => void
  requiredCount?: number // 默认 1
}

export function CaptchaModal({ open, onClose, onVerified, requiredCount = 1 }: CaptchaModalProps) {
  const [challenge, setChallenge] = useState<Challenge | null>(null)
  const [dots, setDots] = useState<Dot[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [collected, setCollected] = useState<string[]>([])
  const masterRef = useRef<HTMLImageElement>(null)
  const total = Math.max(1, requiredCount)

  const fetchChallenge = async () => {
    setLoading(true)
    setError(null)
    setDots([])
    try {
      const c = await apiClient.get<Challenge>('/gapi/public/captcha/challenge')
      setChallenge(c)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'fetch challenge failed')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open) {
      setCollected([])
      void fetchChallenge()
    }
  }, [open])

  const onMasterClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!masterRef.current) return
    const rect = masterRef.current.getBoundingClientRect()
    const x = Math.round(((e.clientX - rect.left) / rect.width) * masterRef.current.naturalWidth)
    const y = Math.round(((e.clientY - rect.top) / rect.height) * masterRef.current.naturalHeight)
    setDots((prev) => [...prev, { x, y }])
  }

  const onSubmit = async () => {
    if (!challenge) return
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.post<{ captcha_token: string }>('/gapi/public/captcha/verify', {
        challenge_id: challenge.challenge_id,
        dots,
      })
      const nextCollected = [...collected, res.captcha_token]
      setCollected(nextCollected)
      if (nextCollected.length >= total) {
        // 验证成功只回调 onVerified;关闭由消费方在 onVerified 内自行处理。
        // ⚠️ 不要在此再调 onClose():GlobalCaptchaGate 把 onClose 映射成
        // captchaBroker.cancelHead(),成功后再调会误取消队列里"下一个文件"的 captcha 请求
        // (多文件并发上传时第二个验证码失败的根因,2026-06-06 fix)。
        onVerified(nextCollected)
      } else {
        // 还差 N - collected 张,拉下一张继续
        void fetchChallenge()
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'verify failed')
      // 失败后重新拉一张(同一张失败则计数不变)
      void fetchChallenge()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="人机验证"
      frameId="rfEyH"
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={fetchChallenge} disabled={loading}>
            换一张
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setDots([])} disabled={loading}>
              清空
            </Button>
            <Button size="sm" onClick={onSubmit} disabled={loading || dots.length === 0}>
              {loading ? '…' : '提交'}
            </Button>
          </div>
        </div>
      }
    >
      {error && <p className="mb-2 text-sm text-red-600">{error}</p>}
      {total > 1 && (
        <p className="mb-2 text-xs text-gray-500">
          第 {Math.min(collected.length + 1, total)} / {total} 张
        </p>
      )}
      {challenge ? (
        <div className="flex flex-col gap-3">
          <div className="text-xs text-gray-500">
            {challenge.prompt}（已点 {dots.length} 个）
          </div>
          <div className="relative inline-block self-center">
            <img
              ref={masterRef}
              src={challenge.image_base64}
              alt="captcha master"
              className="cursor-crosshair select-none"
              onClick={onMasterClick}
              draggable={false}
            />
            {dots.map((d, i) => (
              <span
                key={i}
                className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500 px-2 py-0.5 text-xs font-bold text-white"
                style={{
                  left: `${(d.x / (masterRef.current?.naturalWidth ?? 1)) * 100}%`,
                  top: `${(d.y / (masterRef.current?.naturalHeight ?? 1)) * 100}%`,
                }}
              >
                {i + 1}
              </span>
            ))}
          </div>
          <img
            src={challenge.thumb_base64}
            alt="captcha thumb"
            className="self-center"
          />
        </div>
      ) : (
        <div className="text-center text-sm text-gray-500">{loading ? '加载中…' : '点击下方按钮重试'}</div>
      )}
    </Modal>
  )
}
